import React, { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleanIcon,
  Tune as TuneIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  performanceCache,
  useMemoryOptimization,
  usePerformanceMonitor
} from '../../utils/performanceOptimizer';
import { optimizedStoreService } from '../../services/optimizedStoreService';

interface PerformanceMetrics {
  cacheSize: number;
  memoryUsage: number;
  networkRequests: number;
  renderTime: number;
  cacheHitRate: number;
}

interface OptimizationSettings {
  enableCache: boolean;
  enablePrefetch: boolean;
  enableVirtualization: boolean;
  enableImageOptimization: boolean;
  cacheTimeout: number;
}

const PerformanceOptimizerComponent: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    cacheSize: 0,
    memoryUsage: 0,
    networkRequests: 0,
    renderTime: 0,
    cacheHitRate: 0
  });

  const [settings, setSettings] = useState<OptimizationSettings>({
    enableCache: true,
    enablePrefetch: true,
    enableVirtualization: false,
    enableImageOptimization: true,
    cacheTimeout: 5 * 60 * 1000 // 5 دقائق
  });

  const [optimizing, setOptimizing] = useState(false);
  const [lastOptimization, setLastOptimization] = useState<Date | null>(null);

  const { cleanup } = useMemoryOptimization();
  const { startMeasure, endMeasure, trackRender } = usePerformanceMonitor();

  // تحديث المقاييس
  const updateMetrics = useCallback(() => {
    startMeasure('metrics-update');

    // حساب حجم التخزين المؤقت
    const cacheSize = Object.keys(localStorage).length;

    // تقدير استخدام الذاكرة
    const memoryUsage = (performance as any).memory ?
      (performance as any).memory.usedJSHeapSize / 1024 / 1024 : 0;

    // عدد طلبات الشبكة (تقديري)
    const networkRequests = performance.getEntriesByType('navigation').length +
                           performance.getEntriesByType('resource').length;

    // متوسط وقت الرندر
    const renderTime = performance.getEntriesByType('measure')
      .filter(entry => entry.name.includes('render'))
      .reduce((acc, entry) => acc + entry.duration, 0) /
      Math.max(1, performance.getEntriesByType('measure').length);

    setMetrics({
      cacheSize,
      memoryUsage: Math.round(memoryUsage),
      networkRequests,
      renderTime: Math.round(renderTime * 100) / 100,
      cacheHitRate: 85 // تقديري - يمكن تحسينه
    });

    endMeasure('metrics-update');
  }, [startMeasure, endMeasure]);

  // تحسين الأداء التلقائي
  const optimizePerformance = useCallback(async () => {
    setOptimizing(true);
    startMeasure('performance-optimization');

    try {
      // 1. تنظيف الذاكرة
      cleanup();

      // 2. تنظيف التخزين المؤقت القديم
      performanceCache.cleanup();

      // 3. تنظيف localStorage
      const now = Date.now();
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('_expiry')) {
          const expiry = parseInt(localStorage.getItem(key) || '0');
          if (now > expiry) {
            keysToRemove.push(key);
            keysToRemove.push(key.replace('_expiry', ''));
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));

      // 4. تحسين طلبات الشبكة
      if (settings.enablePrefetch) {
        // تحميل مسبق للبيانات المهمة
        optimizedStoreService.getOptimizedCategories(true);
      }

      // 5. تحديث المقاييس
      updateMetrics();

      setLastOptimization(new Date());

      console.log('✅ Performance optimization completed');
    } catch (error) {
      console.error('❌ Performance optimization failed:', error);
    } finally {
      endMeasure('performance-optimization');
      setOptimizing(false);
    }
  }, [cleanup, settings.enablePrefetch, updateMetrics, startMeasure, endMeasure]);

  // مسح التخزين المؤقت
  const clearCache = useCallback(() => {
    performanceCache.clear();
    localStorage.clear();
    updateMetrics();
    console.log('🧹 Cache cleared');
  }, [updateMetrics]);

  // تحديث الإعدادات
  const updateSetting = useCallback((key: keyof OptimizationSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));

    // حفظ الإعدادات في localStorage
    const newSettings = { ...settings, [key]: value };
    localStorage.setItem('performance_settings', JSON.stringify(newSettings));
  }, [settings]);

  // تحميل الإعدادات عند البدء
  useEffect(() => {
    const savedSettings = localStorage.getItem('performance_settings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (error) {
        console.warn('Failed to load performance settings:', error);
      }
    }

    updateMetrics();

    // تحديث دوري للمقاييس
    const interval = setInterval(updateMetrics, 30000); // كل 30 ثانية

    return () => clearInterval(interval);
  }, [updateMetrics]);

  // تتبع الرندر
  useEffect(() => {
    trackRender();
  });

  const getPerformanceStatus = (value: number, thresholds: { good: number, warning: number }) => {
    if (value <= thresholds.good) return { color: 'success', icon: <CheckIcon /> };
    if (value <= thresholds.warning) return { color: 'warning', icon: <WarningIcon /> };
    return { color: 'error', icon: <WarningIcon /> };
  };

  const memoryStatus = getPerformanceStatus(metrics.memoryUsage, { good: 50, warning: 100 });
  const renderStatus = getPerformanceStatus(metrics.renderTime, { good: 16, warning: 33 });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        ⚡ مُحسِّن الأداء
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" paragraph>
        مراقبة وتحسين أداء التطبيق في الوقت الفعلي
      </Typography>

      {/* مقاييس الأداء */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MemoryIcon color={memoryStatus.color as any} />
                <Box>
                  <Typography variant="h6">{metrics.memoryUsage} MB</Typography>
                  <Typography variant="caption" color="text.secondary">
                    استخدام الذاكرة
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color={renderStatus.color as any} />
                <Box>
                  <Typography variant="h6">{metrics.renderTime}ms</Typography>
                  <Typography variant="caption" color="text.secondary">
                    وقت الرندر
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon color="primary" />
                <Box>
                  <Typography variant="h6">{metrics.cacheSize}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    عناصر التخزين المؤقت
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NetworkIcon color="info" />
                <Box>
                  <Typography variant="h6">{metrics.cacheHitRate}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    معدل نجاح التخزين المؤقت
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* أدوات التحسين */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              🛠️ أدوات التحسين
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={optimizing ? <LinearProgress /> : <TuneIcon />}
                onClick={optimizePerformance}
                disabled={optimizing}
              >
                {optimizing ? 'جاري التحسين...' : 'تحسين تلقائي'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CleanIcon />}
                onClick={clearCache}
                color="warning"
              >
                مسح التخزين المؤقت
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={updateMetrics}
              >
                تحديث المقاييس
              </Button>
            </Box>

            {lastOptimization && (
              <Alert severity="success" sx={{ mb: 2 }}>
                آخر تحسين: {lastOptimization.toLocaleString('ar-EG')}
              </Alert>
            )}

            {optimizing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  جاري تحسين الأداء...
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              ⚙️ إعدادات التحسين
            </Typography>

            <List>
              <ListItem>
                <ListItemText
                  primary="تفعيل التخزين المؤقت"
                  secondary="تخزين البيانات محلياً لتسريع التحميل"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableCache}
                      onChange={(e) => updateSetting('enableCache', e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemText
                  primary="التحميل المسبق"
                  secondary="تحميل البيانات قبل الحاجة إليها"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enablePrefetch}
                      onChange={(e) => updateSetting('enablePrefetch', e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemText
                  primary="التحميل التدريجي"
                  secondary="عرض العناصر المرئية فقط"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableVirtualization}
                      onChange={(e) => updateSetting('enableVirtualization', e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemText
                  primary="تحسين الصور"
                  secondary="ضغط وتحميل الصور بكفاءة"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableImageOptimization}
                      onChange={(e) => updateSetting('enableImageOptimization', e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* نصائح الأداء */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          💡 نصائح لتحسين الأداء
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="استخدم التخزين المؤقت"
                  secondary="يقلل من طلبات الشبكة"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="فعل التحميل المسبق"
                  secondary="يحسن تجربة المستخدم"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="نظف التخزين المؤقت دورياً"
                  secondary="يحرر مساحة الذاكرة"
                />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="استخدم التحميل التدريجي"
                  secondary="للقوائم الطويلة"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="حسن الصور"
                  secondary="يقلل من وقت التحميل"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="راقب الأداء باستمرار"
                  secondary="لاكتشاف المشاكل مبكراً"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PerformanceOptimizerComponent;
